"use client";

import React, { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import Autocomplete, { AutocompleteProps } from "@/components/ui/autocomplete";
import { api } from "@/lib/trpc/client";
export interface AutocompleteSignersInputProps extends Omit<AutocompleteProps, "options" | "onChange"> {
  onValueAction: (option: {
    id: string;
    name: string;
  }) => void;
  institutionId: string;
  referenceOptions?: {
    value: string;
    label: string;
    type: "reference";
  }[];
}
export function AutocompleteSignersInput({
  onValueAction,
  institutionId,
  referenceOptions,
  initialValue,
  ...restProps
}: AutocompleteSignersInputProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const prefetchSignersQuery = api.digitalSignatureRouter.fetchSignersAdmin.useQuery({
    institutionId,
    ids: initialValue ? [initialValue as string] : []
  }, {
    enabled: !!initialValue
  });
  const signersQuery = api.digitalSignatureRouter.fetchSignersAdmin.useQuery({
    institutionId,
    search: debouncedSearchQuery,
    limit: 10
  }, {
    enabled: debouncedSearchQuery.length > 3
  });
  const searchOptions = useMemo(() => {
    if (!signersQuery.data?.length) {
      const prefetchedOptions = prefetchSignersQuery.data?.map(signer => ({
        value: signer.id,
        label: signer.name
      })) ?? [];
      return [...prefetchedOptions, ...(referenceOptions ?? [])];
    }
    return signersQuery.data.map(signer => ({
      value: signer.id,
      label: signer.name
    }));
  }, [signersQuery.data, prefetchSignersQuery.data, referenceOptions]);
  return <Autocomplete {...restProps} isServer initialValue={initialValue ?? ""} options={searchOptions} onQueryChange={setSearchQuery} onChangeAction={val => onValueAction({
    id: val.value as string,
    name: val.label
  })} data-sentry-element="Autocomplete" data-sentry-component="AutocompleteSignersInput" data-sentry-source-file="AutocompleteSignersInput.tsx" />;
}